'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import Cookies from 'js-cookie';
import { parse as parseQuery } from 'hub-http/helpers/params';
import { getCookie } from 'hub-http/helpers/cookies';
import SignupLink from '../SignupLink';
import ErrorMessage from '../ErrorMessage';
import { NO_RECORD_ERROR } from '../../constants/ErrorValues';
import IE11Alert from '../alerts/IE11Alert';
import { fetchLoginRequirement, fetchLoginRequirementOnLoad } from '../../actions/loginRequirementActions';
import RememberMeCheckbox from './RememberMeCheckbox';
import SubmitButton from './SubmitButton';
import EmailInputForm from './EmailInputForm';
import { extractRedirectQueryParams, trimEmail, isValidEmail } from '../../lib/LoginUtils';
import LocalStorage from '../../lib/LocalStorage';
import { getLoginRequirementRequestStatus, getLoginRequirement } from '../../selectors/LoginRequirement';
import { RequestStates } from '../../constants/RequestStates';
import { getSignupUrl } from '../../utils/urls';
import { trackSmartLoginLoad } from '../../lib/tracker';
import { SMART_LOGIN_COOKIE } from '../../constants/SmartLogin';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import selectSessionInvalidationWarning from '../../utils/selectSessionInvalidationWarning';

const EmailEntryPage = props => {
  const dispatch = useDispatch();
  const query = parseQuery(props.location.search.substr(1));
  const redirectQueryParams = extractRedirectQueryParams(query);
  const {
    loginPortalId
  } = redirectQueryParams;
  const {
    useDark,
    allowFbSignup,
    hideSignupLink,
    signupParams,
    page_id,
    authFailureReason
  } = query;
  let loginRedirectUrl = redirectQueryParams.loginRedirectUrl;
  const queryParamError = query['loginError'] || '';
  const sessionInvalidationWarning = selectSessionInvalidationWarning(authFailureReason, loginRedirectUrl);

  if (allowFbSignup && page_id) {
    loginRedirectUrl = `${loginRedirectUrl}&page_id=${page_id}`;
  }

  const [email, setEmail] = useState(query['email'] || '');
  const [isEmailAutofilled, setIsEmailAutofilled] = useState(false);
  const [rememberLogin, setRememberLogin] = useState(Cookies.get('prefs') === '1');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState();
  const [isFormVisible, setIsFormVisible] = useState(false);
  const signupUrl = getSignupUrl(signupParams, allowFbSignup, loginRedirectUrl);
  const status = useSelector(getLoginRequirementRequestStatus);
  const loginRequirement = useSelector(getLoginRequirement);
  const stagedLoginRequirement = LocalStorage.getStagedLoginRequirement();
  const [spinnerTimeout, setSpinnerTimeout] = useState(-1);
  const errorMessages = error || queryParamError;
  const warningMessages = sessionInvalidationWarning;
  useEffect(() => {
    if (status !== RequestStates.REQUESTING && status !== RequestStates.UNATTEMPTED) {
      setIsFormVisible(true);
      setSubmitting(false);
      clearTimeout(spinnerTimeout);
    }

    if (status === RequestStates.FAILED) {
      setError(NO_RECORD_ERROR);
    }
  }, [status, spinnerTimeout]);

  const handleError = newError => {
    setError(newError);
    setSubmitting(false);
  };

  const fetchRequirement = useCallback((inputEmail, inputRememberLogin, isOnLoad) => {
    const data = {
      email: trimEmail(inputEmail),
      rememberLogin: inputRememberLogin
    };
    const queryData = {
      useDark: query.useDark,
      loginRedirectUrl: query.loginRedirectUrl,
      allowFbSignup: query.allowFbSignup,
      hideSignupLink: query.hideSignupLink,
      signupParams: query.signupParams,
      pageId: query.page_id,
      loginPortalId
    };

    if (isOnLoad) {
      dispatch(fetchLoginRequirementOnLoad(data, props.history, queryData));
      const timeout = setTimeout(() => {
        setSubmitting(true);
      }, 1000);
      setSpinnerTimeout(timeout);
    } else {
      dispatch(fetchLoginRequirement(data, props.history, queryData, handleError, loginRequirement));
      setSubmitting(true);
    }
  }, [dispatch, loginPortalId, props, query, loginRequirement]);
  useEffect(() => {
    let stagedEmail = null;

    if (query.email) {
      stagedEmail = query.email;
    } else if (stagedLoginRequirement && stagedLoginRequirement.email) {
      stagedEmail = stagedLoginRequirement.email;
    }

    let stagedRememberLogin = rememberLogin;

    if (stagedLoginRequirement && stagedLoginRequirement.rememberLogin) {
      stagedRememberLogin = stagedLoginRequirement.rememberLogin;
    }

    if (stagedEmail) {
      const locationState = props.history.location.state;
      const isChangeEmail = locationState && locationState.isChangeEmail;

      if (!email) {
        setEmail(stagedEmail);
      }

      if (!isChangeEmail) {
        fetchRequirement(stagedEmail, stagedRememberLogin, true);
        return;
      }
    }

    setIsFormVisible(true); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = e => {
    e.preventDefault();
    fetchRequirement(email, rememberLogin, false);
  };

  useEffect(() => {
    setTimeout(() => {
      try {
        setIsEmailAutofilled(document.querySelectorAll('#username:-webkit-autofill').length > 0); // eslint-disable-next-line no-empty
      } catch (ex) {}
    }, 500); // This allows automation to hook into the page for easy login. (For example speedcurve)

    window._hubspotAutomatedLoginHook = setEmail;
  }, []);

  const isSubmittable = () => {
    const emailSatisfied = isEmailAutofilled || !!email && isValidEmail(email);
    return emailSatisfied;
  };

  const submittable = !submitting && isSubmittable(); // Send tracking request on page load

  useEffect(() => {
    const userEmail = getCookie(SMART_LOGIN_COOKIE, document.cookie);
    trackSmartLoginLoad(userEmail);
  }, []);
  return /*#__PURE__*/_jsxs(NavMarker, {
    name: "SL_EMAIL_ENTRY_SUCCESS",
    children: [isFormVisible && /*#__PURE__*/_jsxs("div", {
      style: {
        height: '100%'
      },
      children: [!hideSignupLink && /*#__PURE__*/_jsx(SignupLink, {
        isFbSignin: allowFbSignup,
        signupParams: signupParams,
        loginRedirectUrlQueryParam: loginRedirectUrl
      }), /*#__PURE__*/_jsxs("form", {
        id: "hs-login",
        onSubmit: onSubmit,
        children: [/*#__PURE__*/_jsx(IE11Alert, {}), /*#__PURE__*/_jsx(EmailInputForm, {
          useDark: useDark,
          onEmailChange: setEmail,
          submitting: submitting,
          email: email
        }), /*#__PURE__*/_jsx("input", {
          type: "password",
          hidden: true
        }), /*#__PURE__*/_jsx(ErrorMessage, {
          error: errorMessages || warningMessages,
          type: errorMessages ? 'danger' : 'info',
          prefix: errorMessages ? 'login.errors' : 'login.warnings',
          signUpLink: signupUrl
        }), /*#__PURE__*/_jsx(RememberMeCheckbox, {
          onChange: () => setRememberLogin(!rememberLogin),
          checked: rememberLogin,
          disabled: submitting
        }), /*#__PURE__*/_jsx(SubmitButton, {
          submitting: submitting,
          disabled: !submittable
        })]
      })]
    }), !isFormVisible && submitting && /*#__PURE__*/_jsx(UILoadingSpinner, {
      className: "m-top-15",
      grow: true,
      minHeight: "56px"
    })]
  });
};

export default EmailEntryPage;